import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Minimal fallback shown while a lazy-loaded landing chunk is fetched.
// Landing pages are code-split (see lazy() imports below) so the initial JS
// bundle doesn't carry ~30 marketing funnels that most sessions never visit.
function LandingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--canvas,#0B1220)] text-[var(--ink-2,#94A3B8)]">
      <span className="spinner dark mr-3" /> Loading…
    </div>
  );
}

// Like <Navigate to={...} replace /> but preserves the current ?search so
// affiliate ?ref= and UTM params survive route aliases.
function NavigateKeepSearch({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}

import Landing from "@/pages/Landing";
import Homepage from "@/pages/marketing/Homepage";
import ProductsIndex from "@/pages/marketing/products/ProductsIndex";
import MktPropertyWorkspacePage from "@/pages/marketing/products/PropertyWorkspacePage";
import MktAiAcquisitionCoachPage from "@/pages/marketing/products/AiAcquisitionCoachPage";
import MktExecutiveDashboardPage from "@/pages/marketing/products/ExecutiveDashboardPage";
import MktMarketIntelligencePage from "@/pages/marketing/products/MarketIntelligencePage";
import MktBuyerIntelligencePage from "@/pages/marketing/products/BuyerIntelligencePage";
import MktPortfolioIntelligencePage from "@/pages/marketing/products/PortfolioIntelligencePage";
import MktStrategyComparisonPage from "@/pages/marketing/products/StrategyComparisonPage";
import SolutionsIndex from "@/pages/marketing/solutions/SolutionsIndex";
import WholesalersSolutionPage from "@/pages/marketing/solutions/WholesalersSolutionPage";
import FixAndFlipSolutionPage from "@/pages/marketing/solutions/FixAndFlipSolutionPage";
import BuyAndHoldSolutionPage from "@/pages/marketing/solutions/BuyAndHoldSolutionPage";
import BrrrrSolutionPage from "@/pages/marketing/solutions/BrrrrSolutionPage";
import CreativeFinanceSolutionPage from "@/pages/marketing/solutions/CreativeFinanceSolutionPage";
import TeamsEnterpriseSolutionPage from "@/pages/marketing/solutions/TeamsEnterpriseSolutionPage";
import PlatformOverviewPage from "@/pages/marketing/PlatformOverviewPage";
import PricingPage from "@/pages/marketing/PricingPage";
import CompanyPage from "@/pages/marketing/CompanyPage";
import ContactPage from "@/pages/marketing/ContactPage";
import ResourcesIndex from "@/pages/marketing/resources/ResourcesIndex";
import BlogShell from "@/pages/marketing/resources/BlogShell";
import DocsShell from "@/pages/marketing/resources/DocsShell";
import CaseStudiesShell from "@/pages/marketing/resources/CaseStudiesShell";
import WebinarsShell from "@/pages/marketing/resources/WebinarsShell";
import ReleaseNotesShell from "@/pages/marketing/resources/ReleaseNotesShell";
import MarketingComingSoonPage from "@/pages/marketing/ComingSoonPage";

// Coming-soon gate. When enabled, all public marketing routes render the
// ComingSoonPage. Authenticated routes (/auth, /login, /app, /platform,
// /legacy, admin surfaces) remain fully accessible. Flip via:
//   frontend/.env → REACT_APP_COMING_SOON=true|false
//
// PREVIEW BYPASS: append ?preview=perrin2026 to ANY marketing URL and the
// gate lifts (also stored to localStorage so it sticks across nav). Great
// for showing the redesign to Tracy/QA without a full redeploy.
const COMING_SOON_ENV = process.env.REACT_APP_COMING_SOON === "true";
const _hasPreviewBypass = (() => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview") === "perrin2026") {
      window.localStorage.setItem("ispeed_preview_bypass", "perrin2026");
      return true;
    }
    return window.localStorage.getItem("ispeed_preview_bypass") === "perrin2026";
  } catch {
    return false;
  }
})();
const COMING_SOON = COMING_SOON_ENV && !_hasPreviewBypass;
const CS = () => <MarketingComingSoonPage />;
import PublicResources from "@/pages/PublicResources";
import PublicResourceDetail from "@/pages/PublicResourceDetail";
import PublicDealRoom from "@/pages/PublicDealRoom";
import PublicPitch from "@/pages/PublicPitch";
import DocuSignDocs from "@/pages/DocuSignDocs";
import Legal from "@/pages/Legal";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import DemoLanding from "@/pages/DemoLanding";
import AppShell from "@/pages/app/AppShell";
import LegacyRedirect from "@/components/LegacyRedirect";
import V3Shell from "@/pages/v3/V3Shell";
// U6-0 — new application shell + design-system landing. Mounted
// at /u6 alongside the live /v3 shell so legacy routes keep working.
const U6AppShell           = lazy(() => import("@/u6/AppShell"));
const U6LandingPage        = lazy(() => import("@/u6/pages/U6LandingPage"));
const U6CommandCenter      = lazy(() => import("@/u6/pages/CommandCenter"));
const U6Pipeline           = lazy(() => import("@/u6/pages/Pipeline"));
const U6DealWorkspaceShell = lazy(() => import("@/u6/workspace/DealWorkspaceShell"));
const U6OverviewTab        = lazy(() => import("@/u6/workspace/tabs/OverviewTab"));
const U6SellerTab          = lazy(() => import("@/u6/workspace/tabs/SellerTab"));
const U6PropertyTab        = lazy(() => import("@/u6/workspace/tabs/PropertyTab"));
const U6CompsARVTab        = lazy(() => import("@/u6/workspace/tabs/CompsARVTab"));
const U6RepairsRehabTab    = lazy(() => import("@/u6/workspace/tabs/RepairsRehabTab"));
const U6UnderwritingTab    = lazy(() => import("@/u6/workspace/tabs/UnderwritingTab"));
const U6StrategiesTab      = lazy(() => import("@/u6/workspace/tabs/StrategiesTab"));
const U6OffersContractsTab = lazy(() => import("@/u6/workspace/tabs/OffersContractsTab"));
const U6DispositionTab     = lazy(() => import("@/u6/workspace/tabs/DispositionTab"));
const U6BuyersTab          = lazy(() => import("@/u6/workspace/tabs/BuyersTab"));
const U6CommunicationsTab  = lazy(() => import("@/u6/workspace/tabs/CommunicationsTab"));
const U6DocumentsTab       = lazy(() => import("@/u6/workspace/tabs/DocumentsTab"));
const U6ActivityTab        = lazy(() => import("@/u6/workspace/tabs/ActivityTab"));
const U6TabComingSoon      = lazy(() => import("@/u6/workspace/tabs/TabComingSoon"));
// U6-6 — Settings
const U6SettingsLanding            = lazy(() => import("@/u6/settings/pages/SettingsLanding"));
const U6ProfileWorkspaceSettings   = lazy(() => import("@/u6/settings/pages/ProfileWorkspaceSettings"));
const U6BrandingSettings           = lazy(() => import("@/u6/settings/pages/BrandingSettings"));
const U6NotificationsSettings      = lazy(() => import("@/u6/settings/pages/NotificationsSettings"));
const U6CommunicationsSettings     = lazy(() => import("@/u6/settings/pages/CommunicationsSettings"));
const U6CommsOverview              = lazy(() => import("@/u6/settings/pages/communications/OverviewTab"));
const U6CommsPhoneNumbers          = lazy(() => import("@/u6/settings/pages/communications/PhoneNumbersTab"));
const U6CommsTwilioAccount         = lazy(() => import("@/u6/settings/pages/communications/TwilioAccountTab"));
const U6CommsSmsMms                = lazy(() => import("@/u6/settings/pages/communications/SmsMmsTab"));
const U6CommsVoiceRecording        = lazy(() => import("@/u6/settings/pages/communications/VoiceRecordingTab"));
const U6CommsNumberBrand           = lazy(() => import("@/u6/settings/pages/communications/NumberToBrandTab"));
const U6CommsA2p                   = lazy(() => import("@/u6/settings/pages/communications/A2pComplianceTab"));
const U6ApexSettings               = lazy(() => import("@/u6/settings/pages/ApexSettings"));
const U6DocuSignSettings           = lazy(() => import("@/u6/settings/pages/integrations/DocuSignSettings"));
const U6WebhookSettings            = lazy(() => import("@/u6/settings/pages/integrations/WebhookSettings"));
const U6TeamSettings               = lazy(() => import("@/u6/settings/pages/TeamSettings"));
const U6DeveloperSettings          = lazy(() => import("@/u6/settings/pages/DeveloperSettings"));
// M2 — release-surface completion
const U6NotFound                   = lazy(() => import("@/u6/shell/U6NotFound"));
const U6DealsIndex                 = lazy(() => import("@/u6/deals/DealsIndex"));
const U6PropertiesIndex            = lazy(() => import("@/u6/properties/PropertiesIndex"));
const _hubs = () => import("@/u6/hubs/landingHubs");
const U6PeopleHub          = lazy(() => _hubs().then((m) => ({ default: m.PeopleHub })));
const U6MarketHub          = lazy(() => _hubs().then((m) => ({ default: m.MarketHub })));
const U6UnderwritingHub    = lazy(() => _hubs().then((m) => ({ default: m.UnderwritingHub })));
const U6OffersHub          = lazy(() => _hubs().then((m) => ({ default: m.OffersHub })));
const U6DispositionHub     = lazy(() => _hubs().then((m) => ({ default: m.DispositionHub })));
const U6CommunicationsHub  = lazy(() => _hubs().then((m) => ({ default: m.CommunicationsHub })));
const U6DocumentsHub       = lazy(() => _hubs().then((m) => ({ default: m.DocumentsHub })));
const U6ActivityHub        = lazy(() => _hubs().then((m) => ({ default: m.ActivityHub })));
const U6ReportsHub         = lazy(() => _hubs().then((m) => ({ default: m.ReportsHub })));
const U6AutomationHub      = lazy(() => _hubs().then((m) => ({ default: m.AutomationHub })));
const U6InboxBridge        = lazy(() => _hubs().then((m) => ({ default: m.InboxBridge })));
const U6AlertsBridge       = lazy(() => _hubs().then((m) => ({ default: m.AlertsBridge })));
const U6SupportBridge      = lazy(() => _hubs().then((m) => ({ default: m.SupportBridge })));
import V3Dashboard from "@/pages/v3/V3Dashboard";
import CalendarPage from "@/pages/v3/CalendarPage";
import PropertiesList from "@/pages/v3/PropertiesList";
import PropertyWorkspace from "@/pages/v3/PropertyWorkspace";
import AcquisitionScorecardPage from "@/pages/v3/AcquisitionScorecardPage";
import ManagerScorecardPage from "@/pages/v3/ManagerScorecardPage";
import ExecutiveDashboardPage from "@/pages/v3/ExecutiveDashboardPage";
import KPIExplorerPage from "@/pages/v3/KPIExplorerPage";
import BuyerIntelligencePage from "@/pages/v3/BuyerIntelligencePage";
import PortfolioIntelligencePage from "@/pages/v3/PortfolioIntelligencePage";
import MarketIntelligencePage from "@/pages/v3/MarketIntelligencePage";
import CoachPage from "@/pages/v3/CoachPage";
import ModuleStub from "@/pages/v3/ModuleStub";
import WholesaleEngine from "@/pages/v3/intel/WholesaleEngine";
import RepairIntelligence from "@/pages/v3/intel/RepairIntelligence";
import DealScorePage from "@/pages/v3/intel/DealScorePage";
import CompsARVPage from "@/pages/v3/intel/CompsARVPage";
import AcquisitionIntelligence from "@/pages/v3/intel/AcquisitionIntelligence";
import OfferIntelligence from "@/pages/v3/intel/OfferIntelligence";
import ExitStrategyIntelligence from "@/pages/v3/intel/ExitStrategyIntelligence";
import MarketplacePage from "@/pages/v3/marketplace/MarketplacePage";
import BusinessLanding from "@/pages/v3/BusinessLanding";
import AdminLanding from "@/pages/v3/AdminLanding";
import {
  Calculator, Wrench, BarChart3, Target, Wand2,
  Handshake, Hammer, Home as HomeIcon, KeyRound as KeyIcon,
  Bell as BellIcon,
} from "lucide-react";
import PlatformShell from "@/pages/platform/PlatformShell";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import PlatformFlags from "@/pages/platform/PlatformFlags";
import PlatformSecurity from "@/pages/platform/PlatformSecurity";
import PlatformWaitlist from "@/pages/platform/PlatformWaitlist";
import {
  PlatformRevenue, PlatformAI, PlatformHealth,
  PlatformSupport, PlatformAnnouncements, PlatformRoles,
} from "@/pages/platform/PlatformMisc";
import {
  PlatformUsers, PlatformPrompts,
} from "@/pages/platform/PlatformUsersAndPrompts";
import { TourProvider } from "@/components/Tour";
import Dashboard from "@/pages/app/Dashboard";
import Inbox from "@/pages/app/Inbox";
import Leads from "@/pages/app/Leads";
import Pipeline from "@/pages/app/Pipeline";
import PropertyPipeline from "@/pages/v3/PropertyPipeline";
import AIDealsPage from "@/pages/v3/AIDealsPage";
import BogFieldServices from "@/pages/v3/BogFieldServices";
import Analyzer from "@/pages/app/Analyzer";
import Portfolio from "@/pages/app/Portfolio";
import Markets from "@/pages/app/Markets";
import Discover from "@/pages/app/Discover";
import Team from "@/pages/app/Team";
import Recordings from "@/pages/app/Recordings";
import Buyers from "@/pages/app/Buyers";
import BuyerSearch from "@/pages/app/BuyerSearch";
import Heatmap from "@/pages/app/Heatmap";
import BuyerDetail from "@/pages/app/BuyerDetail";
import LeadReport from "@/pages/app/LeadReport";
import Settings from "@/pages/app/Settings";
import Analytics from "@/pages/app/Analytics";
import Captures from "@/pages/app/Captures";
import Community from "@/pages/app/Community";
import ForumIndex from "@/pages/app/ForumIndex";
import ForumPostDetail from "@/pages/app/ForumPostDetail";
import Wins from "@/pages/app/Wins";
import Alerts from "@/pages/app/Alerts";
import Billing from "@/pages/app/Billing";
import Funnel from "@/pages/app/Funnel";
import Leaderboard from "@/pages/app/Leaderboard";
import Referrals from "@/pages/app/Referrals";
import DispoRooms from "@/pages/app/DispoRooms";
import DispoRoomDetail from "@/pages/app/DispoRoomDetail";
import DispoBuyersList from "@/pages/app/DispoBuyersList";
import DispoClosings from "@/pages/app/DispoClosings";
import CommandCenter from "@/pages/app/CommandCenter";
import PowerDialer from "@/pages/app/PowerDialer";
import MarketingScripts from "@/pages/app/MarketingScripts";
import BuyerDashboard from "@/pages/BuyerDashboard";
import PublicStreaks from "@/pages/PublicStreaks";
import PublicForumIndex from "@/pages/PublicForumIndex";
import PublicForumPost from "@/pages/PublicForumPost";
import { Toaster } from "@/components/ui/sonner";
import WinsOfTheWeek from "@/pages/WinsOfTheWeek";
import ReferralRedirect from "@/pages/ReferralRedirect";
import AdminLayout from "@/pages/app/admin/AdminLayout";
import AdminUsers from "@/pages/app/admin/AdminUsers";
import AdminRevenue from "@/pages/app/admin/AdminRevenue";
import AdminActivity from "@/pages/app/admin/AdminActivity";
import AdminHealth from "@/pages/app/admin/AdminHealth";
import AdminWebhooks from "@/pages/app/admin/AdminWebhooks";
import AdminContentStudio from "@/pages/app/admin/AdminContentStudio";
import AdminUserCleanup from "@/pages/app/admin/AdminUserCleanup";
import AdminInboundWebhooks from "@/pages/app/admin/AdminInboundWebhooks";
import NumbersAuditPage from "@/pages/admin/NumbersAuditPage";
import AdminOutreachTemplates from "@/pages/app/admin/AdminOutreachTemplates";
import AdminFlags from "@/pages/app/admin/AdminFlags";
import AdminVipLinks from "@/pages/app/admin/AdminVipLinks";
import AdminSupport from "@/pages/app/admin/AdminSupport";
import Support from "@/pages/app/Support";
import AdminFunnel from "@/pages/app/admin/AdminFunnel";
import AdminDemoFunnel from "@/pages/app/admin/AdminDemoFunnel";
import AdminAuditLog from "@/pages/app/admin/AdminAuditLog";
import AdminEmailAB from "@/pages/app/admin/AdminEmailAB";
import AdminEmailFunnels from "@/pages/app/admin/AdminEmailFunnels";
import AdminEmailOps from "@/pages/app/admin/AdminEmailOps";
import AdminEmailPreview from "@/pages/app/admin/AdminEmailPreview";
import AdminEmailJobs from "@/pages/app/admin/AdminEmailJobs";
import PublicWins from "@/pages/PublicWins";
import PublicReport from "@/pages/PublicReport";
import PublicSnapshot from "@/pages/PublicSnapshot";
import ComingSoon from "@/pages/app/ComingSoon";
import Drips from "@/pages/app/Drips";
import DripLibrary from "@/pages/app/DripLibrary";
import Sequences from "@/pages/app/Sequences";
import Tasks from "@/pages/app/Tasks";
import Marketing from "@/pages/app/Marketing";
import Contracts from "@/pages/app/Contracts";
import TransactionsBoard from "@/pages/v3/dispositions/TransactionsBoard";
import BetaCockpitPage from "@/pages/v3/admin/BetaCockpitPage";
import BetaStatusPage from "@/pages/marketing/BetaStatusPage";
import CallScriptSettingsPage from "@/pages/v3/settings/CallScriptSettingsPage";
import Resources from "@/pages/app/Resources";
import ResourceDetail from "@/pages/app/ResourceDetail";
import ResourceCertificate from "@/pages/app/ResourceCertificate";
import AdminResources from "@/pages/app/admin/AdminResources";
import AdminOverview from "@/pages/app/admin/AdminOverview";
import AdminUserDeepView from "@/pages/app/admin/AdminUserDeepView";
import AdminAbTests from "@/pages/app/admin/AdminAbTests";
import AdminAffiliates from "@/pages/app/admin/AdminAffiliates";
import AdminPodcasterPartners from "@/pages/app/admin/AdminPodcasterPartners";
import AdminChangelog from "@/pages/app/admin/AdminChangelog";
import AdminEbookSurvey from "@/pages/app/admin/AdminEbookSurvey";
import AdminMotivatedSellerAB from "@/pages/app/admin/AdminMotivatedSellerAB";
import AdminMarketplace from "@/pages/app/admin/AdminMarketplace";
import AdminApexCalls from "@/pages/app/admin/AdminApexCalls";
import ApexPage from "@/pages/app/Apex";
// ----- Lazy-loaded marketing landing pages ------------------------------
// These ~29 landing pages are routed public URLs (ad funnels, ebook magnets,
// affiliate paths) referenced from live email drips and distributed PDFs, so
// we keep every route alive but code-split them. Only the shell + the
// landing someone actually visits gets downloaded. Shrinks initial bundle
// meaningfully without 404'ing any outbound link.
import LibraryBundleSuccess from "@/pages/LibraryBundleSuccess";
import StarterPackSuccess from "@/pages/StarterPackSuccess";
import AppPaidLibrary from "@/pages/app/AppPaidLibrary";
import AppLibrary from "@/pages/app/AppLibrary";
import AdminServiceInquiries from "@/pages/app/admin/AdminServiceInquiries";
import AdminValuation from "@/pages/app/admin/AdminValuation";
import AdminLaunchKit from "@/pages/app/admin/AdminLaunchKit";
import AdminSocialMedia from "@/pages/app/admin/AdminSocialMedia";
import ServiceStatusPage from "@/pages/ServiceStatusPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import Affiliate from "@/pages/app/Affiliate";
import ApiDocs from "@/pages/app/ApiDocs";
import Compare from "@/pages/compare/Compare";
import CompareDetail from "@/pages/compare/CompareDetail";
import FreeEbook from "@/pages/FreeEbook";
import WholesaleCrashCourse from "@/pages/WholesaleCrashCourse";
import TcpaGuide from "@/pages/TcpaGuide";
const LibraryBundleLanding = lazy(() => import("@/pages/LibraryBundleLanding"));
const StarterPackLanding = lazy(() => import("@/pages/StarterPackLanding"));
const CashBuyersLanding = lazy(() => import("@/pages/CashBuyersLanding"));
const AffiliatesLanding = lazy(() => import("@/pages/AffiliatesLanding"));
const GettingStartedLanding = lazy(() => import("@/pages/GettingStartedLanding"));
const MotivatedSellerLanding = lazy(() => import("@/pages/MotivatedSellerLanding"));
const MotivatedSellerFastLanding = lazy(() => import("@/pages/MotivatedSellerFastLanding"));
const MotivatedSellerGuideLanding = lazy(() => import("@/pages/MotivatedSellerGuideLanding"));
const W2To10KLanding = lazy(() => import("@/pages/W2To10KLanding"));
const CashBuyerBlackbookLanding = lazy(() => import("@/pages/CashBuyerBlackbookLanding"));
const PriorityRestartLanding = lazy(() => import("@/pages/PriorityRestartLanding"));
const WholesalingOneOhOneLanding = lazy(() => import("@/pages/WholesalingOneOhOneLanding"));
const MarketingMachineLanding = lazy(() => import("@/pages/MarketingMachineLanding"));
const ColdCallScriptBookLanding = lazy(() => import("@/pages/ColdCallScriptBookLanding"));
const SellerObjectionsPlaybookLanding = lazy(() => import("@/pages/SellerObjectionsPlaybookLanding"));
const AfterFirstCheckLanding = lazy(() => import("@/pages/AfterFirstCheckLanding"));
const HandsOffWholesalerLanding = lazy(() => import("@/pages/HandsOffWholesalerLanding"));
const VirtualWholesalerLanding = lazy(() => import("@/pages/VirtualWholesalerLanding"));
const WealthBlueprintLanding = lazy(() => import("@/pages/WealthBlueprintLanding"));
const InvestorFuelLanding = lazy(() => import("@/pages/InvestorFuelLanding"));
const MiddletonLanding = lazy(() => import("@/pages/MiddletonLanding"));
const VelocityPlayLanding = lazy(() => import("@/pages/VelocityPlayLanding"));
const RealEstateProsLanding = lazy(() => import("@/pages/RealEstateProsLanding"));
const DispoStudioLanding = lazy(() => import("@/pages/DispoStudioLanding"));
const PowerDialerLanding = lazy(() => import("@/pages/PowerDialerLanding"));
const FundingPartnersLanding = lazy(() => import("@/pages/FundingPartnersLanding"));
const LibraryLanding = lazy(() => import("@/pages/LibraryLanding"));
const ProductHuntLanding = lazy(() => import("@/pages/ProductHuntLanding"));
const MarketplaceLanding = lazy(() => import("@/pages/MarketplaceLanding"));
// -----------------------------------------------------------------------
import PublicChangelog from "@/pages/PublicChangelog";
import EbookSurvey from "@/pages/EbookSurvey";
import Marketplace from "@/pages/app/Marketplace";
import LeadsTrash from "@/pages/app/LeadsTrash";
import BuyersTrash from "@/pages/app/BuyersTrash";
import UpgradeToPro from "@/pages/app/UpgradeToPro";
import TeamWorkspace from "@/pages/app/TeamWorkspace";
import AcceptInvite from "@/pages/AcceptInvite";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-2)]">
        <span className="spinner dark mr-3" /> Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LandingFallback />}>
    <Routes>
      {/* Public "beta status" page — always reachable, even during
          coming-soon lockdown, so waitlisters can see momentum. */}
      <Route path="/beta-status" element={<BetaStatusPage />} />
      <Route path="/beta" element={<Navigate to="/login" replace />} />
      <Route path="/" element={COMING_SOON ? <CS /> : <Homepage />} />
      <Route path="/products" element={COMING_SOON ? <CS /> : <ProductsIndex />} />
      <Route path="/products/property-workspace" element={COMING_SOON ? <CS /> : <MktPropertyWorkspacePage />} />
      <Route path="/products/ai-acquisition-coach" element={COMING_SOON ? <CS /> : <MktAiAcquisitionCoachPage />} />
      <Route path="/products/executive-dashboard" element={COMING_SOON ? <CS /> : <MktExecutiveDashboardPage />} />
      <Route path="/products/market-intelligence" element={COMING_SOON ? <CS /> : <MktMarketIntelligencePage />} />
      <Route path="/products/buyer-intelligence" element={COMING_SOON ? <CS /> : <MktBuyerIntelligencePage />} />
      <Route path="/products/portfolio-intelligence" element={COMING_SOON ? <CS /> : <MktPortfolioIntelligencePage />} />
      <Route path="/products/strategy-comparison" element={COMING_SOON ? <CS /> : <MktStrategyComparisonPage />} />
      <Route path="/solutions" element={COMING_SOON ? <CS /> : <SolutionsIndex />} />
      <Route path="/solutions/wholesalers" element={COMING_SOON ? <CS /> : <WholesalersSolutionPage />} />
      <Route path="/solutions/fix-and-flip" element={COMING_SOON ? <CS /> : <FixAndFlipSolutionPage />} />
      <Route path="/solutions/buy-and-hold" element={COMING_SOON ? <CS /> : <BuyAndHoldSolutionPage />} />
      <Route path="/solutions/brrrr" element={COMING_SOON ? <CS /> : <BrrrrSolutionPage />} />
      <Route path="/solutions/creative-finance" element={COMING_SOON ? <CS /> : <CreativeFinanceSolutionPage />} />
      <Route path="/solutions/teams-enterprise" element={COMING_SOON ? <CS /> : <TeamsEnterpriseSolutionPage />} />
      <Route path="/platform-overview" element={COMING_SOON ? <CS /> : <PlatformOverviewPage />} />
      <Route path="/pricing" element={COMING_SOON ? <CS /> : <PricingPage />} />
      <Route path="/company" element={COMING_SOON ? <CS /> : <CompanyPage />} />
      <Route path="/about" element={COMING_SOON ? <CS /> : <CompanyPage />} />
      <Route path="/contact" element={COMING_SOON ? <CS /> : <ContactPage />} />
      <Route path="/resources" element={COMING_SOON ? <CS /> : <ResourcesIndex />} />
      <Route path="/resources/blog" element={COMING_SOON ? <CS /> : <BlogShell />} />
      <Route path="/resources/docs" element={COMING_SOON ? <CS /> : <DocsShell />} />
      <Route path="/resources/case-studies" element={COMING_SOON ? <CS /> : <CaseStudiesShell />} />
      <Route path="/resources/webinars" element={COMING_SOON ? <CS /> : <WebinarsShell />} />
      <Route path="/resources/release-notes" element={COMING_SOON ? <CS /> : <ReleaseNotesShell />} />
      <Route path="/legacy/resources" element={<PublicResources />} />
      <Route path="/legacy/resources/:slug" element={<PublicResourceDetail />} />
      <Route path="/legacy" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/library-bundle" element={<LibraryBundleLanding />} />
      <Route path="/library-bundle/success" element={<LibraryBundleSuccess />} />
      <Route path="/starter-pack" element={<StarterPackLanding />} />
      <Route path="/starter-pack/success" element={<StarterPackSuccess />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/signup" element={<Navigate to="/register" replace />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/r/:token" element={<PublicReport />} />
      <Route path="/s/:token" element={<PublicSnapshot />} />
      <Route path="/deal/:slug" element={<PublicDealRoom />} />
      <Route path="/pitch/:slug" element={<PublicPitch />} />
      <Route path="/docusign" element={<DocuSignDocs />} />
      <Route path="/buyers/me" element={<BuyerDashboard />} />
      <Route path="/wins/:token" element={<PublicWins />} />
      <Route path="/leaderboards/streaks" element={<PublicStreaks />} />
      <Route path="/wins-of-the-week" element={<WinsOfTheWeek />} />
      <Route path="/forum" element={<PublicForumIndex />} />
      <Route path="/forum/:slug" element={<PublicForumPost />} />
      <Route path="/refer/:code" element={<ReferralRedirect />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/compare/:slug" element={<CompareDetail />} />
      {/* /vs/:slug — SEO alias for competitor comparison pages (e.g. /vs/resimpli) */}
      <Route path="/vs/:slug" element={<CompareDetail />} />
      <Route path="/legal/:slug" element={<Legal />} />
      <Route path="/free-ebook" element={<FreeEbook />} />
      <Route path="/wholesale-crash-course" element={<WholesaleCrashCourse />} />
      <Route path="/tcpa-guide" element={<TcpaGuide />} />
      <Route path="/getting-started" element={<GettingStartedLanding />} />
      <Route path="/start" element={<NavigateKeepSearch to="/getting-started" />} />
      <Route path="/motivated-seller" element={<MotivatedSellerLanding />} />
      <Route path="/motivated-seller/fast" element={<MotivatedSellerFastLanding />} />
      <Route path="/motivated-seller/guide" element={<MotivatedSellerGuideLanding />} />
      <Route path="/first-deal-fast" element={<NavigateKeepSearch to="/motivated-seller/fast" />} />
      <Route path="/how-to-find-motivated-sellers" element={<NavigateKeepSearch to="/motivated-seller/guide" />} />
      <Route path="/motivated-sellers" element={<NavigateKeepSearch to="/motivated-seller" />} />
      <Route path="/30-days" element={<NavigateKeepSearch to="/motivated-seller" />} />
      <Route path="/first-deal" element={<NavigateKeepSearch to="/motivated-seller" />} />
      <Route path="/from-w2-to-10k" element={<W2To10KLanding />} />
      <Route path="/quit-w2" element={<NavigateKeepSearch to="/from-w2-to-10k" />} />
      <Route path="/cash-buyer-blackbook" element={<CashBuyerBlackbookLanding />} />
      <Route path="/blackbook" element={<NavigateKeepSearch to="/cash-buyer-blackbook" />} />
      <Route path="/priority-restart" element={<PriorityRestartLanding />} />
      <Route path="/founder-restart" element={<NavigateKeepSearch to="/priority-restart" />} />
      <Route path="/restart" element={<NavigateKeepSearch to="/priority-restart" />} />
      <Route path="/wholesaling-101" element={<WholesalingOneOhOneLanding />} />
      <Route path="/w101" element={<NavigateKeepSearch to="/wholesaling-101" />} />
      <Route path="/marketing-machine" element={<MarketingMachineLanding />} />
      <Route path="/marketing" element={<NavigateKeepSearch to="/marketing-machine" />} />
      <Route path="/cold-call-scripts" element={<ColdCallScriptBookLanding />} />
      <Route path="/scripts" element={<NavigateKeepSearch to="/cold-call-scripts" />} />
      <Route path="/seller-objections" element={<SellerObjectionsPlaybookLanding />} />
      <Route path="/objections" element={<NavigateKeepSearch to="/seller-objections" />} />
      <Route path="/after-first-check" element={<AfterFirstCheckLanding />} />
      <Route path="/first-check" element={<NavigateKeepSearch to="/after-first-check" />} />
      <Route path="/hands-off-wholesaler" element={<HandsOffWholesalerLanding />} />
      <Route path="/hands-off" element={<NavigateKeepSearch to="/hands-off-wholesaler" />} />
      <Route path="/virtual-wholesaler" element={<VirtualWholesalerLanding />} />
      <Route path="/virtual" element={<NavigateKeepSearch to="/virtual-wholesaler" />} />
      <Route path="/wealth-blueprint" element={<WealthBlueprintLanding />} />
      <Route path="/blueprint" element={<NavigateKeepSearch to="/wealth-blueprint" />} />
      <Route path="/investor-fuel" element={<InvestorFuelLanding />} />
      <Route path="/investorfuel" element={<NavigateKeepSearch to="/investor-fuel" />} />
      <Route path="/middleton" element={<MiddletonLanding />} />
      <Route path="/middleton-method" element={<NavigateKeepSearch to="/middleton" />} />
      <Route path="/philly-builder" element={<NavigateKeepSearch to="/middleton" />} />
      <Route path="/velocity-play" element={<VelocityPlayLanding />} />
      <Route path="/velocity" element={<NavigateKeepSearch to="/velocity-play" />} />
      <Route path="/velocitybanking" element={<NavigateKeepSearch to="/velocity-play" />} />
      <Route path="/real-estate-pros" element={<RealEstateProsLanding />} />
      <Route path="/realestatepros" element={<NavigateKeepSearch to="/real-estate-pros" />} />
      <Route path="/repros" element={<NavigateKeepSearch to="/real-estate-pros" />} />
      <Route path="/dispo-studio" element={<DispoStudioLanding />} />
      <Route path="/dispo" element={<NavigateKeepSearch to="/dispo-studio" />} />
      <Route path="/power-dialer" element={<PowerDialerLanding />} />
      <Route path="/dialer" element={<NavigateKeepSearch to="/power-dialer" />} />
      <Route path="/vs/mojo-dialer" element={<NavigateKeepSearch to="/power-dialer" />} />
      <Route path="/vs/readymode" element={<NavigateKeepSearch to="/power-dialer" />} />
      <Route path="/funding" element={<FundingPartnersLanding />} />
      <Route path="/pof" element={<NavigateKeepSearch to="/funding" />} />
      <Route path="/library" element={<LibraryLanding />} />
      <Route path="/ebooks" element={<NavigateKeepSearch to="/library" />} />
      <Route path="/product-hunt" element={<ProductHuntLanding />} />
      <Route path="/launch" element={<NavigateKeepSearch to="/product-hunt" />} />
      <Route path="/marketplace" element={<MarketplaceLanding />} />
      <Route path="/changelog" element={<PublicChangelog />} />
      <Route path="/ebook-survey/:captureId" element={<EbookSurvey />} />
      <Route path="/affiliates" element={<AffiliatesLanding />} />
      <Route path="/ebook" element={<Navigate to="/free-ebook" replace />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/services/thanks" element={<ServiceStatusPage kind="paid" />} />
      <Route path="/services/cancelled" element={<ServiceStatusPage kind="cancelled" />} />
      <Route path="/cash-buyers" element={<CashBuyersLanding />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      {/* iSpeedToClose 3.0 — Project Velocity */}
      <Route
        path="/v3"
        element={
          <ProtectedRoute>
            <V3Shell />
          </ProtectedRoute>
        }
      >
        <Route index element={<V3Dashboard />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="properties" element={<PropertiesList />} />
        {/* CRM — legacy operator tools re-homed inside V3 shell (Wave 1) */}
        <Route path="crm/marketplace" element={<MarketplacePage />} />
        <Route path="crm/leads" element={<Leads />} />
        <Route path="crm/leads/trash" element={<LeadsTrash />} />
        <Route path="crm/leads/:id/report" element={<LeadReport />} />
        <Route path="crm/discover" element={<Discover />} />
        <Route path="crm/dialer" element={<ApexPage />} />
        <Route path="crm/buyers" element={<Buyers />} />
        <Route path="crm/buyers/trash" element={<BuyersTrash />} />
        <Route path="crm/buyers/:id" element={<BuyerDetail />} />
        <Route path="crm/pipeline" element={<PropertyPipeline />} />
        <Route path="crm/ai-deals" element={<AIDealsPage />} />
        <Route path="field-services" element={<BogFieldServices />} />
        <Route path="crm/pipeline-legacy" element={<Pipeline />} />
        <Route path="crm/deal-rooms" element={<DispoRooms />} />
        <Route path="crm/deal-rooms/:id" element={<DispoRoomDetail />} />
        <Route path="crm/contracts" element={<Contracts />} />
        <Route path="contracts" element={<Navigate to="/v3/crm/contracts" replace />} />
        <Route path="dispo/transactions" element={<TransactionsBoard />} />
        <Route path="crm/transactions" element={<Navigate to="/v3/dispo/transactions" replace />} />
        <Route path="crm/closings" element={<DispoClosings />} />
        <Route path="crm/inbox" element={<Inbox />} />
        {/* Wave 4 — Outreach / Marketing suite (shell-wrapped legacy) */}
        <Route path="crm/outreach/sequences"        element={<Sequences />} />
        <Route path="crm/outreach/drips"            element={<Drips />} />
        <Route path="crm/outreach/drip-library"     element={<DripLibrary />} />
        <Route path="crm/outreach/scripts"          element={<MarketingScripts />} />
        <Route path="crm/outreach/marketing"        element={<Marketing />} />
        <Route path="crm/outreach/captures"         element={<Captures />} />
        <Route path="crm/outreach/recordings"       element={<Recordings />} />
        {/* Wave 3 — Operator Tools + Settings (shell-wrapped legacy) */}
        <Route path="tools/analyzer"        element={<Analyzer />} />
        <Route path="tools/portfolio"       element={<Portfolio />} />
        <Route path="tools/tasks"           element={<Tasks />} />
        <Route path="tools/command-center"  element={<CommandCenter />} />
        <Route path="settings"              element={<Settings />} />
        {/* Wave 5 (partial) — Growth: Referrals + Affiliates */}
        <Route path="growth/referrals"      element={<Referrals />} />
        <Route path="growth/affiliate"      element={<Affiliate />} />
        {/* Wave 3 (deferred) — Market Discovery: Markets picker + Heatmap */}
        <Route path="markets"               element={<Markets />} />
        <Route path="markets/heatmap"       element={<Heatmap />} />
        {/* Wave 6 — CRM detail pages under V3 */}
        <Route path="crm/leads/trash"           element={<LeadsTrash />} />
        <Route path="crm/leads/:id/report"      element={<LeadReport />} />
        <Route path="crm/buyers/search"         element={<BuyerSearch />} />
        <Route path="crm/buyers/trash"          element={<BuyersTrash />} />
        <Route path="crm/buyers/:id"            element={<BuyerDetail />} />
        <Route path="crm/dispo/:id"             element={<DispoRoomDetail />} />
        <Route path="crm/dispo-buyers"          element={<DispoBuyersList />} />
        {/* Wave 5 remainder — Community + Growth extras */}
        <Route path="community"                 element={<Community />} />
        <Route path="community/forum"           element={<ForumIndex />} />
        <Route path="community/forum/:postId"   element={<ForumPostDetail />} />
        <Route path="growth/wins"               element={<Wins />} />
        <Route path="growth/leaderboard"        element={<Leaderboard />} />
        {/* Wave 7 — Business & Ops */}
        <Route path="business/apex"             element={<ApexPage />} />
        <Route path="business/analytics"        element={<Analytics />} />
        <Route path="business/funnel"           element={<Funnel />} />
        <Route path="business/workspace"        element={<TeamWorkspace />} />
        <Route path="business/team"             element={<Team />} />
        <Route path="business/billing"          element={<Billing />} />
        <Route path="business/upgrade"          element={<UpgradeToPro />} />
        {/* Wave 8 — Alerts, Learn, Support */}
        <Route path="alerts"                    element={<Alerts />} />
        <Route path="learn/resources"           element={<Resources />} />
        <Route path="learn/resources/:slug"     element={<ResourceDetail />} />
        <Route path="learn/resources/certificate/:trackId" element={<ResourceCertificate />} />
        <Route path="learn/library"             element={<Navigate to="/v3/learn/paid-library" replace />} />
        <Route path="learn/paid-library"        element={<AppPaidLibrary />} />
        <Route path="support"                   element={<Support />} />
        <Route path="support/:ticketId"         element={<Support />} />
        <Route path="api-docs"                  element={<ApiDocs />} />
        {/* Wave 9 — Admin Console under V3 (nested with all sub-routes) */}
        <Route path="admin/console" element={<AdminLayout />}>
          <Route index                         element={<AdminOverview />} />
          <Route path="users"                  element={<AdminUsers />} />
          <Route path="users/:userId"          element={<AdminUserDeepView />} />
          <Route path="revenue"                element={<AdminRevenue />} />
          <Route path="valuation"              element={<AdminValuation />} />
          <Route path="launch-kit"             element={<AdminLaunchKit />} />
          <Route path="social-media"           element={<AdminSocialMedia />} />
          <Route path="activity"               element={<AdminActivity />} />
          <Route path="funnel"                 element={<AdminFunnel />} />
          <Route path="demo-funnel"            element={<AdminDemoFunnel />} />
          <Route path="audit"                  element={<AdminAuditLog />} />
          <Route path="health"                 element={<AdminHealth />} />
          <Route path="webhooks"               element={<AdminWebhooks />} />
          <Route path="content-studio"         element={<AdminContentStudio />} />
          <Route path="user-cleanup"           element={<AdminUserCleanup />} />
          <Route path="inbound-webhooks"       element={<AdminInboundWebhooks />} />
          <Route path="outreach-templates"     element={<AdminOutreachTemplates />} />
          <Route path="flags"                  element={<AdminFlags />} />
          <Route path="vip-links"              element={<AdminVipLinks />} />
          <Route path="affiliates"             element={<AdminAffiliates />} />
          <Route path="podcasters"             element={<AdminPodcasterPartners />} />
          <Route path="changelog"              element={<AdminChangelog />} />
          <Route path="ebook-survey"           element={<AdminEbookSurvey />} />
          <Route path="marketing/motivated-seller" element={<AdminMotivatedSellerAB />} />
          <Route path="marketplace"            element={<AdminMarketplace />} />
          <Route path="apex-calls"             element={<AdminApexCalls />} />
          <Route path="services"               element={<AdminServiceInquiries />} />
          <Route path="support"                element={<AdminSupport />} />
          <Route path="support/:ticketId"      element={<AdminSupport />} />
          <Route path="resources"              element={<AdminResources />} />
          <Route path="ab-tests"               element={<AdminAbTests />} />
          <Route path="email" element={<AdminEmailOps />}>
            <Route index                       element={<AdminEmailAB />} />
            <Route path="ab"                   element={<AdminEmailAB />} />
            <Route path="funnels"              element={<AdminEmailFunnels />} />
            <Route path="preview"              element={<AdminEmailPreview />} />
            <Route path="jobs"                 element={<AdminEmailJobs />} />
          </Route>
        </Route>
        <Route path="properties/:id" element={<PropertyWorkspace />} />
        {/* Friendlier alias — /v3/deal/:id and /v3/deals/:id both open the
            unified Deal Card (formerly buried at /v3/properties/:id).
            Every intel/strategy page is now a tab inside this workspace. */}
        <Route path="deal/:id" element={<PropertyWorkspace />} />
        <Route path="deals/:id" element={<PropertyWorkspace />} />
        {/* Internal QA — Numbers Audit (any authenticated user in preview) */}
        <Route path="admin/qa/numbers" element={<NumbersAuditPage />} />
        <Route path="acquisition" element={<AcquisitionScorecardPage />} />
        <Route path="acquisition/managers/:id" element={<ManagerScorecardPage />} />
        <Route path="executive" element={<ExecutiveDashboardPage />} />
        <Route path="kpi" element={<KPIExplorerPage />} />
        <Route path="buyers" element={<BuyerIntelligencePage />} />
        <Route path="portfolio" element={<PortfolioIntelligencePage />} />
        <Route path="market" element={<MarketIntelligencePage />} />
        <Route path="coach" element={<CoachPage />} />
        {/* Intelligence Engines */}
        <Route path="intel/wholesale-engine" element={<WholesaleEngine />} />
        <Route path="intel/repair" element={<RepairIntelligence />} />
        <Route path="intel/comps-arv" element={<CompsARVPage />} />
        <Route path="intel/deal-score" element={<DealScorePage />} />
        <Route path="intel/acquisition-intel" element={<AcquisitionIntelligence />} />
        <Route path="intel/offer-intel" element={<OfferIntelligence />} />
        <Route path="intel/exit-intel" element={<ExitStrategyIntelligence />} />
        <Route path="intel/ai-recommendations" element={
          <ModuleStub
            testid="v3-stub-intel-ai-recommendations"
            slug="ai-recommendations"
            status="preview"
            icon={Wand2}
            title="AI Recommendations"
            description="Proactive next-best-action suggestions based on your pipeline, buyer heat, and market conditions."
            bullets={[
              "Coach-authored recommendations refreshed hourly",
              "Impact estimate ($, days-to-close) per suggestion",
              "One-click apply into the workspace",
            ]}
          />
        } />
        {/* Strategy Center — execution workspaces (stubs) */}
        <Route path="strategy/wholesale" element={
          <ModuleStub
            testid="v3-stub-strategy-wholesale"
            slug="wholesale"
            status="beta"
            icon={Handshake}
            title="Wholesale Workspace"
            description="End-to-end execution: contract, buyer marketing, assignment, and closing coordination."
            bullets={[
              "Purchase contract → assignment → HUD in one flow",
              "Buyer blast with click + reply tracking",
              "Title company handoff with docs checklist",
            ]}
          />
        } />
        <Route path="strategy/fix-flip" element={
          <ModuleStub
            testid="v3-stub-strategy-fix-flip"
            slug="fix-flip"
            status="early-access"
            icon={Hammer}
            title="Fix & Flip Workspace"
            description="Rehab budget, schedule, subs and re-list pricing in one project view."
            bullets={[
              "Gantt schedule with sub trades and inspections",
              "Live budget-vs-actual with vendor invoices",
              "Re-list pricing tuned by comp velocity",
            ]}
          />
        } />
        <Route path="strategy/brrrr" element={
          <ModuleStub
            testid="v3-stub-strategy-brrrr"
            slug="brrrr"
            status="early-access"
            icon={HomeIcon}
            title="Rental / BRRRR Workspace"
            description="Full BRRRR loop — buy, rehab, rent, refi, repeat — with cash-flow forecast."
            bullets={[
              "Pro-forma with taxes, insurance, vacancy and management",
              "Refi eligibility tracker (LTV, seasoning, DSCR)",
              "Tenant screening + lease template automation",
            ]}
          />
        } />
        <Route path="strategy/creative-finance" element={
          <ModuleStub
            testid="v3-stub-strategy-creative-finance"
            slug="creative-finance"
            status="preview"
            icon={KeyIcon}
            title="Creative Finance Workspace"
            description="Seller finance, subject-to, and hybrid structures with automated docs."
            bullets={[
              "Structure builder — SF, sub-to, wrap, novation",
              "Auto-generated notes, mortgages and disclosures",
              "Payment tracker with automated ACH pulls",
            ]}
          />
        } />
        {/* Business + Administration landings */}
        <Route path="business" element={<BusinessLanding />} />
        <Route path="admin" element={<AdminLanding />} />
        <Route path="admin/beta" element={<BetaCockpitPage />} />
        <Route path="settings/call-script" element={<CallScriptSettingsPage />} />
        <Route path="notifications" element={
          <ModuleStub
            testid="v3-stub-notifications"
            icon={BellIcon}
            title="Notifications"
            description="Your alerts, mentions, and system messages — filterable and searchable."
            bullets={["Filter by type, project, priority",
                     "Snooze and pin critical alerts",
                     "Digest email daily / weekly"]}
          />
        } />
      </Route>

      {/* U6-0 — Application shell + design system (sibling to /v3).
          Legacy routes untouched; later U6 waves (Command Center,
          Pipeline, Deal Workspace) mount under /u6/*.  */}
      <Route
        path="/u6"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LandingFallback />}>
              <U6AppShell />
            </Suspense>
          </ProtectedRoute>
        }
      >
        {/* U6-1: index now renders the real Command Center. The U6-0
            dev-only landing stays reachable at /u6/legacy-landing in
            case it is useful during QA. */}
        <Route index element={
          <Suspense fallback={<LandingFallback />}>
            <U6CommandCenter />
          </Suspense>
        } />
        <Route path="pipeline" element={
          <Suspense fallback={<LandingFallback />}>
            <U6Pipeline />
          </Suspense>
        } />
        <Route path="deals/:dealId" element={
          <Suspense fallback={<LandingFallback />}>
            <U6DealWorkspaceShell />
          </Suspense>
        }>
          <Route index element={null} />
          <Route path="overview" element={<Suspense fallback={null}><U6OverviewTab /></Suspense>} />
          <Route path="seller"   element={<Suspense fallback={null}><U6SellerTab /></Suspense>} />
          <Route path="property" element={<Suspense fallback={null}><U6PropertyTab /></Suspense>} />
          {/* 10 later-wave tabs render legitimate readiness shells. */}
          <Route path="comps"            element={<Suspense fallback={null}><U6CompsARVTab /></Suspense>} />
          <Route path="rehab"            element={<Suspense fallback={null}><U6RepairsRehabTab /></Suspense>} />
          <Route path="underwriting"     element={<Suspense fallback={null}><U6UnderwritingTab /></Suspense>} />
          <Route path="strategies"       element={<Suspense fallback={null}><U6StrategiesTab /></Suspense>} />
          <Route path="offers-contracts" element={<Suspense fallback={null}><U6OffersContractsTab /></Suspense>} />
          <Route path="disposition"      element={<Suspense fallback={null}><U6DispositionTab /></Suspense>} />
          <Route path="buyers"           element={<Suspense fallback={null}><U6BuyersTab /></Suspense>} />
          <Route path="communications"   element={<Suspense fallback={null}><U6CommunicationsTab /></Suspense>} />
          <Route path="documents"        element={<Suspense fallback={null}><U6DocumentsTab /></Suspense>} />
          <Route path="activity"         element={<Suspense fallback={null}><U6ActivityTab /></Suspense>} />
        </Route>
        <Route path="legacy-landing" element={
          <Suspense fallback={<LandingFallback />}>
            <U6LandingPage />
          </Suspense>
        } />

        {/* U6-6 — Settings */}
        <Route path="settings" element={<Suspense fallback={<LandingFallback />}><U6SettingsLanding /></Suspense>} />
        <Route path="settings/profile" element={<Suspense fallback={<LandingFallback />}><U6ProfileWorkspaceSettings /></Suspense>} />
        <Route path="settings/branding" element={<Suspense fallback={<LandingFallback />}><U6BrandingSettings /></Suspense>} />
        <Route path="settings/notifications" element={<Suspense fallback={<LandingFallback />}><U6NotificationsSettings /></Suspense>} />
        <Route path="settings/apex" element={<Suspense fallback={<LandingFallback />}><U6ApexSettings /></Suspense>} />
        <Route path="settings/team" element={<Suspense fallback={<LandingFallback />}><U6TeamSettings /></Suspense>} />
        <Route path="settings/developer" element={<Suspense fallback={<LandingFallback />}><U6DeveloperSettings /></Suspense>} />
        <Route path="settings/integrations/docusign" element={<Suspense fallback={<LandingFallback />}><U6DocuSignSettings /></Suspense>} />
        <Route path="settings/integrations/webhooks" element={<Suspense fallback={<LandingFallback />}><U6WebhookSettings /></Suspense>} />
        <Route path="settings/communications" element={<Suspense fallback={<LandingFallback />}><U6CommunicationsSettings /></Suspense>}>
          <Route index           element={<Suspense fallback={null}><U6CommsOverview /></Suspense>} />
          <Route path="numbers"  element={<Suspense fallback={null}><U6CommsPhoneNumbers /></Suspense>} />
          <Route path="twilio"   element={<Suspense fallback={null}><U6CommsTwilioAccount /></Suspense>} />
          <Route path="sms"      element={<Suspense fallback={null}><U6CommsSmsMms /></Suspense>} />
          <Route path="voice"    element={<Suspense fallback={null}><U6CommsVoiceRecording /></Suspense>} />
          <Route path="brand-mapping" element={<Suspense fallback={null}><U6CommsNumberBrand /></Suspense>} />
          <Route path="a2p"      element={<Suspense fallback={null}><U6CommsA2p /></Suspense>} />
        </Route>

        {/* M2 — release-surface completion */}
        <Route path="deals"          element={<Suspense fallback={<LandingFallback />}><U6DealsIndex /></Suspense>} />
        <Route path="properties"     element={<Suspense fallback={<LandingFallback />}><U6PropertiesIndex /></Suspense>} />
        <Route path="people"         element={<Suspense fallback={<LandingFallback />}><U6PeopleHub /></Suspense>} />
        <Route path="market"         element={<Suspense fallback={<LandingFallback />}><U6MarketHub /></Suspense>} />
        <Route path="underwriting"   element={<Suspense fallback={<LandingFallback />}><U6UnderwritingHub /></Suspense>} />
        <Route path="offers"         element={<Suspense fallback={<LandingFallback />}><U6OffersHub /></Suspense>} />
        <Route path="disposition"    element={<Suspense fallback={<LandingFallback />}><U6DispositionHub /></Suspense>} />
        <Route path="communications" element={<Suspense fallback={<LandingFallback />}><U6CommunicationsHub /></Suspense>} />
        <Route path="documents"      element={<Suspense fallback={<LandingFallback />}><U6DocumentsHub /></Suspense>} />
        <Route path="activity"       element={<Suspense fallback={<LandingFallback />}><U6ActivityHub /></Suspense>} />
        <Route path="reports"        element={<Suspense fallback={<LandingFallback />}><U6ReportsHub /></Suspense>} />
        <Route path="automation"     element={<Suspense fallback={<LandingFallback />}><U6AutomationHub /></Suspense>} />
        <Route path="inbox"          element={<Suspense fallback={<LandingFallback />}><U6InboxBridge /></Suspense>} />
        <Route path="alerts"         element={<Suspense fallback={<LandingFallback />}><U6AlertsBridge /></Suspense>} />
        <Route path="support"        element={<Suspense fallback={<LandingFallback />}><U6SupportBridge /></Suspense>} />
        {/* In-shell 404 — catches ANY unmatched /u6/* route. MUST stay last. */}
        <Route path="*"              element={<Suspense fallback={<LandingFallback />}><U6NotFound /></Suspense>} />
      </Route>

      <Route
        path="/platform"
        element={
          <ProtectedRoute>
            <PlatformShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<PlatformDashboard />} />
        <Route path="tenants" element={<PlatformTenants />} />
        <Route path="users" element={<PlatformUsers />} />
        <Route path="revenue" element={<PlatformRevenue />} />
        <Route path="ai" element={<PlatformAI />} />
        <Route path="prompts" element={<PlatformPrompts />} />
        <Route path="health" element={<PlatformHealth />} />
        <Route path="audit" element={<PlatformAudit />} />
        <Route path="support" element={<PlatformSupport />} />
        <Route path="flags" element={<PlatformFlags />} />
        <Route path="announcements" element={<PlatformAnnouncements />} />
        <Route path="roles" element={<PlatformRoles />} />
        <Route path="security" element={<PlatformSecurity />} />
        <Route path="waitlist" element={<PlatformWaitlist />} />
      </Route>
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <LegacyRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <LegacyRedirect />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      {/* Mount once at the root so toast.success / toast.error calls
          throughout the app actually render a notification (sonner requires
          its portal to exist somewhere in the tree). */}
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
